<template>
  <div class="legal-page">
    <!-- Header -->
    <header class="main-header" :class="{ 'scrolled': isScrolled }">
      <div class="header-inner">
        <!-- Logo -->
        <router-link to="/" class="brand-logo-link">
          <div class="logo-mark">
            <svg viewBox="0 0 100 100" class="diamond-svg">
              <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" stroke-linecap="round" />
              <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" stroke-linecap="round" />
              <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
              <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
            </svg>
          </div>
          <div class="logo-text-block">
            <div class="brand-name">JEWEL<span class="brand-highlight">BIZ</span></div>
            <div class="brand-subtext">SMART JEWELLERY BUSINESS MANAGEMENT</div>
          </div>
        </router-link>

        <!-- Desktop Nav -->
        <nav class="desktop-nav d-none d-lg-flex">
          <router-link to="/" class="nav-link">
            <v-icon x-small class="mr-1">mdi-arrow-left</v-icon>
            Back to Home
          </router-link>
          <router-link to="/terms-conditions" class="nav-link">Terms & Conditions</router-link>
          <router-link to="/book-demo" class="nav-link">Book a Demo</router-link>
        </nav>

        <!-- Right Header Actions -->
        <div class="header-actions">
          <router-link to="/" class="btn-back-nav d-inline-flex d-lg-none mr-2">
            <v-icon small class="mr-1">mdi-arrow-left</v-icon>
            Home
          </router-link>
          <router-link to="/login" class="btn-signin-gold">
            Sign In
            <v-icon small class="ml-1">mdi-arrow-right</v-icon>
          </router-link>
        </div>
      </div>
    </header>

    <!-- Hero Banner -->
    <section class="legal-hero">
      <div class="container-luxury">
        <div class="legal-kicker">
          <span class="kicker-sparkle">✦</span>
          <span>LEGAL & COMPLIANCE</span>
          <span class="kicker-sparkle">✦</span>
        </div>
        <h1 class="legal-title">Privacy Policy</h1>
        <p class="legal-subtitle">
          How JewelBiz protects, processes, and secures your jewellery showroom, inventory, and customer data.
        </p>
        <div class="meta-badge-row">
          <span class="meta-pill"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-shield-check</v-icon> ISO 27001 Certified Security Standards</span>
          <span class="meta-pill"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-calendar-check</v-icon> Last Updated: September 2026</span>
          <span class="meta-pill"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-lock-outline</v-icon> 256-Bit SSL/TLS Encryption</span>
        </div>
      </div>
    </section>

    <!-- Main Content Grid -->
    <section class="legal-content-section">
      <div class="container-luxury">
        <div class="legal-layout-grid">
          <!-- Sidebar Navigation -->
          <aside class="legal-sidebar d-none d-md-block">
            <div class="sidebar-sticky-card">
              <h4 class="sidebar-heading">Document Contents</h4>
              <ul class="toc-list">
                <li><a href="#section-1" :class="{ 'active': activeSection === 'section-1' }">1. Overview & Scope</a></li>
                <li><a href="#section-2" :class="{ 'active': activeSection === 'section-2' }">2. Information We Collect</a></li>
                <li><a href="#section-3" :class="{ 'active': activeSection === 'section-3' }">3. How We Use Data</a></li>
                <li><a href="#section-4" :class="{ 'active': activeSection === 'section-4' }">4. Showroom Data Ownership</a></li>
                <li><a href="#section-5" :class="{ 'active': activeSection === 'section-5' }">5. Security & Cloud Backups</a></li>
                <li><a href="#section-6" :class="{ 'active': activeSection === 'section-6' }">6. Third-Party Integrations</a></li>
                <li><a href="#section-7" :class="{ 'active': activeSection === 'section-7' }">7. Data Retention & Portability</a></li>
                <li><a href="#section-8" :class="{ 'active': activeSection === 'section-8' }">8. Contact & Data Officer</a></li>
              </ul>

              <div class="sidebar-action-box mt-6">
                <router-link to="/terms-conditions" class="sidebar-link-btn">
                  View Terms & Conditions →
                </router-link>
              </div>
            </div>
          </aside>

          <!-- Main Document Body -->
          <div class="legal-body-card">
            <!-- Section 1 -->
            <div id="section-1" class="legal-section">
              <div class="section-number-tag">01</div>
              <h2 class="section-title">Overview & Scope</h2>
              <p>
                Welcome to <strong>JewelBiz</strong> ("Company", "we", "us", or "our"), a specialized cloud-based Jewellery Enterprise Resource Planning (ERP) platform. We are committed to protecting the privacy, confidentiality, and commercial secrecy of jewellery retailers, wholesalers, manufacturers, staff members, and their retail clients.
              </p>
              <p>
                This Privacy Policy explains how our platform collects, uses, encrypts, and handles business information, customer transaction logs, inventory records, gold savings scheme records, and Karigar workshop balances across our web and mobile applications.
              </p>
            </div>

            <!-- Section 2 -->
            <div id="section-2" class="legal-section">
              <div class="section-number-tag">02</div>
              <h2 class="section-title">Information We Collect</h2>
              <p>To provide high-precision jewellery billing, stock tracking, and taxation services, JewelBiz processes the following categories of data:</p>
              
              <div class="info-block-grid">
                <div class="info-card">
                  <div class="info-icon-wrap"><v-icon color="#9E6D2B">mdi-storefront</v-icon></div>
                  <h4 class="info-card-title">Showroom & Business Profile</h4>
                  <p>Store legal name, branch addresses, contact numbers, GSTIN, BIS Hallmark HUID licenses, custom tax rates, and authorized bank accounts.</p>
                </div>

                <div class="info-card">
                  <div class="info-icon-wrap"><v-icon color="#9E6D2B">mdi-account-group</v-icon></div>
                  <h4 class="info-card-title">Customer & Scheme Records</h4>
                  <p>Customer contact information, billing history, gold savings scheme passbooks, monthly installment logs, nominee details, and redemption history.</p>
                </div>

                <div class="info-card">
                  <div class="info-icon-wrap"><v-icon color="#9E6D2B">mdi-diamond-stone</v-icon></div>
                  <h4 class="info-card-title">Inventory & Workshop Data</h4>
                  <p>Item purity (24K, 22K/916, 18K, Silver, Platinum), gross/net weights, wastage (V.A.), stone weight/clarity, HUID barcodes, and Karigar issue/receipt metal ledgers.</p>
                </div>

                <div class="info-card">
                  <div class="info-icon-wrap"><v-icon color="#9E6D2B">mdi-shield-account</v-icon></div>
                  <h4 class="info-card-title">User Credentials & Audit Logs</h4>
                  <p>Staff login credentials (bcrypt encrypted), role permissions (Admin, Sales, Billing, Karigar, Inventory), login timestamps, IP addresses, and operational audit logs.</p>
                </div>
              </div>
            </div>

            <!-- Section 3 -->
            <div id="section-3" class="legal-section">
              <div class="section-number-tag">03</div>
              <h2 class="section-title">How We Use Your Data</h2>
              <p>All information stored in JewelBiz is strictly utilized to operate and power your jewellery showroom operations:</p>
              <ul class="styled-legal-list">
                <li><strong>Automated GST Invoicing & Hallmarking:</strong> Generating statutory tax invoices, calculating SGST/CGST/IGST, and recording unique 6-character alphanumeric HUID tags.</li>
                <li><strong>Karigar Metal Ledger Balancing:</strong> Tracking gold issue weights, pure conversion ratios, melting losses, and scrap recovery.</li>
                <li><strong>Live Board Rate Synchronization:</strong> Fetching realtime bullion spot rates and applying showroom board adjustments.</li>
                <li><strong>Automated Customer Alerts:</strong> Sending SMS/WhatsApp/Email notifications for monthly scheme dues, invoice receipts, and festival offers.</li>
                <li><strong>Security & Audit Trails:</strong> Maintaining immutable transaction audit logs to safeguard your business against internal discrepancies.</li>
              </ul>
            </div>

            <!-- Section 4 -->
            <div id="section-4" class="legal-section">
              <div class="section-number-tag">04</div>
              <h2 class="section-title">Showroom Data Ownership & Non-Disclosure</h2>
              <div class="callout-gold-box">
                <v-icon color="#9E6D2B" class="mr-2">mdi-check-decagram</v-icon>
                <strong>100% Tenant Ownership Guarantee:</strong> You retain complete and exclusive ownership of your customer lists, sales records, margins, and inventory data.
              </div>
              <p>
                We adhere to strict confidentiality standards. We <strong>never sell, lease, rent, trade, or monetize</strong> your commercial data, customer phone numbers, or inventory valuations to any third party or marketing agency.
              </p>
            </div>

            <!-- Section 5 -->
            <div id="section-5" class="legal-section">
              <div class="section-number-tag">05</div>
              <h2 class="section-title">Security & Cloud Backup Architecture</h2>
              <p>We deploy bank-grade security protocols to protect your business assets 24/7:</p>
              <ul class="styled-legal-list">
                <li><strong>End-to-End Transport Security:</strong> All data transmitted between your showroom devices and our servers is encrypted using 256-bit TLS/HTTPS encryption.</li>
                <li><strong>Automated Redundant Backups:</strong> Daily automated database snapshots stored securely on encrypted AWS S3 cloud storage.</li>
                <li><strong>Role-Based Access Control (RBAC):</strong> Multi-tier staff privilege segregation preventing unauthorized view of profit margins or wholesale ledger costs.</li>
                <li><strong>Cryptographic Password Hashing:</strong> Zero plain-text password storage using industry-standard salted bcrypt algorithms.</li>
              </ul>
            </div>

            <!-- Section 6 -->
            <div id="section-6" class="legal-section">
              <div class="section-number-tag">06</div>
              <h2 class="section-title">Third-Party Integrations</h2>
              <p>
                JewelBiz may integrate with trusted third-party providers strictly for functional platform features:
              </p>
              <ul class="styled-legal-list">
                <li><strong>Bullion Rate Providers (GoldAPI.io):</strong> Real-time international and domestic spot gold and silver pricing feeds.</li>
                <li><strong>Cloud Infrastructure (AWS & MongoDB Atlas):</strong> Highly available, ISO-compliant cloud database and file storage clusters.</li>
                <li><strong>Communication Gateways:</strong> Transactional SMS, WhatsApp Business API, and Gmail SMTP for automated alerts and PDF invoice dispatches.</li>
              </ul>
            </div>

            <!-- Section 7 -->
            <div id="section-7" class="legal-section">
              <div class="section-number-tag">07</div>
              <h2 class="section-title">Data Retention & Portability</h2>
              <p>
                Your data is retained for the active duration of your showroom SaaS subscription. Showroom owners can export their entire customer ledger, sales summaries, scheme accounts, and stock registries into standard Excel (.xlsx) and PDF formats at any time directly through the admin portal.
              </p>
              <p>
                Upon account termination or cancellation, you may request complete data purging, which will be executed within 30 business days following statutory tax retention periods.
              </p>
            </div>

            <!-- Section 8 -->
            <div id="section-8" class="legal-section">
              <div class="section-number-tag">08</div>
              <h2 class="section-title">Contact & Data Protection Officer</h2>
              <p>
                If you have questions regarding this Privacy Policy, your showroom's data security, or wish to exercise data rights, please contact our Data Governance Team:
              </p>
              <div class="contact-card-box">
                <div class="contact-row">
                  <v-icon small color="#9E6D2B" class="mr-2">mdi-domain</v-icon>
                  <strong>JewelBiz</strong>
                </div>
                <div class="contact-row">
                  <v-icon small color="#9E6D2B" class="mr-2">mdi-email-outline</v-icon>
                  <span>Email: <a href="mailto:support@jewelbiz.com">support@jewelbiz.com</a></span>
                </div>
                <div class="contact-row">
                  <v-icon small color="#9E6D2B" class="mr-2">mdi-phone</v-icon>
                  <span>Phone: <a href="tel:+919633845820">+91 9633845820</a></span>
                </div>
                <div class="contact-row">
                  <v-icon small color="#9E6D2B" class="mr-2">mdi-map-marker-outline</v-icon>
                  <span>Kochi, Kerala, India</span>
                </div>
              </div>
            </div>

            <div class="doc-footer-actions">
              <router-link to="/terms-conditions" class="btn-doc-link">
                Read Terms & Conditions →
              </router-link>
              <router-link to="/book-demo" class="btn-doc-primary">
                Book a Free Software Demo
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="legal-page-footer">
      <div class="container-luxury footer-flex">
        <p class="footer-copy">
          &copy; {{ new Date().getFullYear() }} JewelBiz. All rights reserved.
        </p>
        <div class="footer-nav-links">
          <router-link to="/">Home</router-link>
          <span class="dot">•</span>
          <router-link to="/terms-conditions">Terms & Conditions</router-link>
          <span class="dot">•</span>
          <router-link to="/book-demo">Book Demo</router-link>
          <span class="dot">•</span>
          <router-link to="/login">Login</router-link>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  name: "PrivacyPolicy",
  data() {
    return {
      isScrolled: false,
      activeSection: "section-1"
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    this.handleScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 40;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700;800&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap');

.legal-page {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  background-color: #FAF8F5;
  color: #1E1815;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.container-luxury {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  width: 100%;
}

/* Header */
.main-header {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background: rgba(250, 248, 245, 0.94);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid rgba(158, 109, 43, 0.12);
  transition: all 0.3s ease;
}

.main-header.scrolled {
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 4px 20px rgba(158, 109, 43, 0.08);
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.brand-logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.logo-mark {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.diamond-svg {
  width: 100%;
  height: 100%;
}

.logo-text-block {
  display: flex;
  flex-direction: column;
}

.brand-name {
  font-family: 'Cinzel', serif;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2.2px;
  color: #1E1815;
  line-height: 1.1;
}

.brand-highlight {
  color: #9E6D2B;
}

.brand-subtext {
  font-size: 8.5px;
  font-weight: 600;
  letter-spacing: 1.2px;
  color: #7D551E;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 24px;
}

.nav-link {
  font-size: 14px;
  font-weight: 600;
  color: #554438;
  text-decoration: none;
  transition: color 0.2s ease;
  display: inline-flex;
  align-items: center;
}

.nav-link:hover {
  color: #9E6D2B;
}

.btn-signin-gold {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: linear-gradient(135deg, #B27E36 0%, #8E5E20 100%);
  color: #FFFFFF !important;
  font-size: 13px;
  font-weight: 600;
  border-radius: 30px;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(158, 109, 43, 0.25);
  transition: all 0.25s ease;
}

.btn-signin-gold:hover {
  background: linear-gradient(135deg, #C28F43 0%, #9B6925 100%);
  transform: translateY(-1px);
}

.btn-back-nav {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  background: rgba(158, 109, 43, 0.08);
  border: 1px solid rgba(158, 109, 43, 0.25);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #7D551E;
  text-decoration: none;
}

/* Hero */
.legal-hero {
  padding: 60px 0 40px;
  background: linear-gradient(180deg, #F5EFE6 0%, #FAF8F5 100%);
  border-bottom: 1px solid #EFE8DD;
  text-align: center;
}

.legal-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 2.5px;
  color: #9E6D2B;
  margin-bottom: 12px;
}

.legal-title {
  font-family: 'Cinzel', serif;
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 12px;
  line-height: 1.2;
}

.legal-subtitle {
  font-size: clamp(15px, 2vw, 17px);
  color: #6B5E55;
  max-width: 680px;
  margin: 0 auto 24px;
  line-height: 1.6;
}

.meta-badge-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px;
}

.meta-pill {
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #FFFFFF;
  border: 1px solid rgba(158, 109, 43, 0.25);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #554438;
  box-shadow: 0 2px 6px rgba(158, 109, 43, 0.05);
}

/* Layout Grid */
.legal-content-section {
  padding: 50px 0 80px;
  flex: 1;
}

.legal-layout-grid {
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: 40px;
  align-items: flex-start;
}

@media (max-width: 960px) {
  .legal-layout-grid {
    grid-template-columns: 1fr;
  }
}

/* Sidebar */
.sidebar-sticky-card {
  position: sticky;
  top: 90px;
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 6px 20px rgba(30, 24, 21, 0.04);
}

.sidebar-heading {
  font-family: 'Cinzel', serif;
  font-size: 15px;
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 16px;
  letter-spacing: 0.5px;
}

.toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.toc-list a {
  font-size: 13.5px;
  font-weight: 500;
  color: #554438;
  text-decoration: none;
  display: block;
  padding: 6px 10px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.toc-list a:hover {
  background: rgba(158, 109, 43, 0.08);
  color: #9E6D2B;
}

.sidebar-link-btn {
  display: block;
  text-align: center;
  padding: 10px 14px;
  background: rgba(158, 109, 43, 0.08);
  border: 1px solid rgba(158, 109, 43, 0.3);
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #7D551E;
  text-decoration: none;
  transition: all 0.2s ease;
}

.sidebar-link-btn:hover {
  background: #9E6D2B;
  color: #FFFFFF;
}

/* Body Card */
.legal-body-card {
  background: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 20px;
  padding: clamp(28px, 4vw, 48px);
  box-shadow: 0 8px 30px rgba(30, 24, 21, 0.04);
}

.legal-section {
  position: relative;
  padding-bottom: 36px;
  margin-bottom: 36px;
  border-bottom: 1px solid #F0EAE1;
}

.legal-section:last-of-type {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-number-tag {
  display: inline-block;
  font-family: 'Cinzel', serif;
  font-size: 12px;
  font-weight: 700;
  color: #9E6D2B;
  letter-spacing: 1px;
  margin-bottom: 4px;
}

.section-title {
  font-family: 'Cinzel', serif;
  font-size: clamp(20px, 2.5vw, 24px);
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 16px;
  line-height: 1.3;
}

.legal-section p {
  font-size: 15px;
  line-height: 1.75;
  color: #4A3E36;
  margin-bottom: 14px;
}

.styled-legal-list {
  padding-left: 20px;
  margin: 14px 0 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.styled-legal-list li {
  font-size: 14.5px;
  line-height: 1.7;
  color: #4A3E36;
}

.styled-legal-list strong {
  color: #1E1815;
}

.info-block-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 16px;
  margin: 20px 0;
}

.info-card {
  background: #FAF7F2;
  border: 1px solid #EFE8DD;
  border-radius: 12px;
  padding: 18px;
}

.info-icon-wrap {
  margin-bottom: 8px;
}

.info-card-title {
  font-family: 'Cinzel', serif;
  font-size: 15px;
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 6px;
}

.info-card p {
  font-size: 13px;
  color: #554438;
  line-height: 1.6;
  margin-bottom: 0;
}

.callout-gold-box {
  background: linear-gradient(135deg, rgba(245, 235, 220, 0.7) 0%, rgba(255, 248, 235, 0.9) 100%);
  border-left: 4px solid #9E6D2B;
  border-radius: 8px;
  padding: 14px 18px;
  margin: 16px 0;
  font-size: 14.5px;
  color: #5A3F1A;
  display: flex;
  align-items: center;
}

.contact-card-box {
  background: #FAF7F2;
  border: 1px solid #EFE8DD;
  border-radius: 12px;
  padding: 20px;
  margin-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.contact-row {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #4A3E36;
}

.contact-row a {
  color: #9E6D2B;
  font-weight: 600;
  text-decoration: none;
}

.doc-footer-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 40px;
  padding-top: 24px;
  border-top: 1px solid #EFE8DD;
}

.btn-doc-link {
  font-size: 14px;
  font-weight: 600;
  color: #9E6D2B;
  text-decoration: none;
}

.btn-doc-primary {
  padding: 10px 22px;
  background: linear-gradient(135deg, #B27E36 0%, #8E5E20 100%);
  color: #FFFFFF !important;
  border-radius: 25px;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(158, 109, 43, 0.22);
}

/* Footer */
.legal-page-footer {
  background: #FAF7F2;
  border-top: 1px solid #EFE8DD;
  padding: 24px 0;
}

.footer-flex {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.footer-copy {
  font-size: 13px;
  color: #7D6F64;
  margin-bottom: 0;
}

.footer-nav-links {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
}

.footer-nav-links a {
  color: #554438;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.footer-nav-links a:hover {
  color: #9E6D2B;
}

.dot {
  color: #C5BAAD;
}
</style>
